import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

const initialState = {
  list: [],
  loading: false,
  error: "",
};

const unwrapToArray = (payload) => {
  const candidates = [
    payload,
    payload?.data,
    payload?.data?.data,
    payload?.data?.result,
    payload?.result,
    payload?.list,
    payload?.items,
    payload?.data?.items,
    payload?.data?.list,
    payload?.data?.records,
    payload?.records,
    payload?.Data,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    if (candidate && typeof candidate === "object") {
      const firstArray = Object.values(candidate).find((v) => Array.isArray(v));
      if (Array.isArray(firstArray)) return firstArray;
    }
  }

  return [];
};

// Fetch pending notifications by composing existing leave and expense endpoints
export const loadPendingNotifications = createAsyncThunk(
  "notification/loadPendingNotifications",
  async (employeeId, { rejectWithValue }) => {
    try {
      console.log('loadPendingNotifications STARTED with employeeId:', employeeId);
      
      const [leaveRes, expenseRes, resignationRes] = await Promise.all([
        axios.get(
          `leave/EmployeeLeaveGetByReporting?EmployeeId=${employeeId || 0}&page=1&limit=10`
        ).catch(err => {
          console.error('Leave API Error:', err);
          throw err;
        }),
        axios.get("expense-form?page=1&count=10&approvalStatus=PENDING").catch(err => {
          console.error('Expense API Error:', err);
          return { data: [] };
        }),
        axios.get("employee/GetEmployeeResignationList?page=1&limit=20&status=PENDING").catch(err => {
          console.error('Resignation API Error:', err);
          return { data: [] };
        }),
      ]);
      
      console.log('API Responses received:', {
        leaveRes: leaveRes?.data,
        expenseRes: expenseRes?.data,
        leaveResType: typeof leaveRes?.data,
        isLeaveArray: Array.isArray(leaveRes?.data)
      });

      const leavePayload = leaveRes?.data;
      const expensePayload = expenseRes?.data;
      const resignationPayload = resignationRes?.data;

      // Debug logging
      console.log('Notification API Response:', {
        leavePayload,
        expensePayload,
        isLeaveArray: Array.isArray(leavePayload),
        isExpenseArray: Array.isArray(expensePayload),
        leaveResData: leaveRes?.data,
        expenseResData: expenseRes?.data
      });

      // Capture raw payloads - handle both direct arrays and wrapped responses
      const leavesRaw = Array.isArray(leavePayload) 
        ? leavePayload 
        : unwrapToArray(leavePayload || {}) || [];
      const expensesRaw = Array.isArray(expensePayload)
        ? expensePayload
        : unwrapToArray(expensePayload || {}) || [];
      const resignationsRaw = Array.isArray(resignationPayload)
        ? resignationPayload
        : unwrapToArray(resignationPayload || {}) || [];

      console.log('Unwrapped arrays:', {
        leavesRaw,
        expensesRaw,
        leavesCount: leavesRaw.length,
        expensesCount: expensesRaw.length
      });

      // Filter ONLY pending items BEFORE normalizing
      // LeaveStatusId: 0 = Rejected, 1 = Approved, 3 = Pending
      const pendingLeaves = (Array.isArray(leavesRaw) ? leavesRaw : []).filter((item) => {
        const statusId = Number(item?.LeaveStatusId);
        const statusName = String(item?.LeaveStatusName || '').toLowerCase();
        return statusId === 3 || statusName === 'pending';
      });

      const pendingExpenses = (Array.isArray(expensesRaw) ? expensesRaw : []).filter((item) => {
        const status = String(item?.approvalStatus || item?.ApprovalStatus || '').toLowerCase();
        return status === 'pending';
      });

      const pendingResignations = (Array.isArray(resignationsRaw) ? resignationsRaw : []).filter((item) => {
        const status = String(item?.overallStatus || item?.status || item?.Status || '').toLowerCase();
        return status === 'pending';
      });

      console.log('Filtered pending items:', {
        pendingLeavesCount: pendingLeaves.length,
        pendingExpensesCount: pendingExpenses.length,
        pendingLeaves,
        pendingExpenses
      });

      const normalizedLeaves = pendingLeaves.map((item) => {
        const normalized = {
          type: "leave",
          refId:
            item?.EmployeeLeaveId ??
            item?.LeaveId ??
            item?.id ??
            item?.Id ??
            item?.EmployeeId,
          status: item?.LeaveStatusName ?? item?.status ?? item?.Status ?? "PENDING",
          // Preserve status ID for filtering (keep as-is, can be string or number)
          LeaveStatusId: item?.LeaveStatusId,
          StatusId: item?.LeaveStatusId,
          title: `${item?.LeaveTypeName || "Leave"} request`,
          meta: item?.EmployeeName || item?.EmployeeFullName || item?.userName || "Leave applicant",
          createdAt:
            item?.CreatedDate ||
            item?.createdDate ||
            item?.StartDate ||
            item?.CreatedAt ||
            item?.createdAt,
          raw: item,
        };
        
        // Debug each normalized leave
        console.log('Normalizing leave:', {
          original: item,
          normalized,
          LeaveStatusId: item?.LeaveStatusId,
          LeaveStatusName: item?.LeaveStatusName
        });
        
        return normalized;
      });

      const normalizedExpenses = pendingExpenses.map((item) => ({
        type: "expense",
        refId: item?.ExpenseId ?? item?.expenseId ?? item?.Id ?? item?.id,
        status: item?.approvalStatus ?? item?.ApprovalStatus ?? item?.status ?? "PENDING",
        title: item?.Title || item?.ExpenseTypeName || "Expense claim",
        meta: item?.user?.firstName
          ? `${item?.user?.firstName} ${item?.user?.lastName || ""}`.trim()
          : item?.EmployeeName || "Expense applicant",
        createdAt: item?.createdAt || item?.CreatedAt || item?.ExpenseDate,
        raw: item,
      }));

      const normalizedResignations = pendingResignations.map((item) => ({
        type: "resignation",
        refId: item?.id ?? item?.ResignationId ?? item?.resignationId,
        status: item?.overallStatus ?? item?.status ?? item?.Status ?? "PENDING",
        title: "Resignation request",
        meta: item?.employeeName ?? item?.EmployeeName ?? item?.userName ?? "Employee",
        createdAt: item?.submittedAt ?? item?.SubmittedAt ?? item?.createdAt ?? item?.CreatedAt,
        raw: item,
      }));

      const combined = [...normalizedLeaves, ...normalizedExpenses, ...normalizedResignations];
      
      console.log('Normalized notifications:', {
        normalizedLeaves,
        normalizedExpenses,
        combined,
        combinedCount: combined.length,
        pendingLeaves: normalizedLeaves.filter(l => 
          l.status?.toLowerCase() === 'pending' || 
          l.LeaveStatusId === 3 || 
          l.LeaveStatusId === 0
        )
      });

      // Always return original payload alongside normalized list for easier debugging
      const result = {
        list: combined,
        meta: {
          leavePayload,
          expensePayload,
          resignationPayload,
        },
      };
      
      console.log('loadPendingNotifications RETURNING:', {
        result,
        combinedCount: combined.length,
        combined
      });
      
      return result;
    } catch (error) {
      console.error('loadPendingNotifications ERROR:', error);
      const errorPayload = error?.response?.data || { message: error?.message || "Unable to load notifications" };
      console.error('Rejecting with:', errorPayload);
      return rejectWithValue(errorPayload);
    }
  }
);

const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    clearNotificationError: (state) => {
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadPendingNotifications.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(loadPendingNotifications.fulfilled, (state, action) => {
      console.log('loadPendingNotifications FULFILLED:', {
        action,
        payload: action.payload,
        payloadType: typeof action.payload,
        isArray: Array.isArray(action.payload)
      });
      
      state.loading = false;
      const payload = action.payload || {};
      // Support both legacy array return and new { list, meta }
      const list = Array.isArray(payload) ? payload : payload.list || [];
      
      console.log('Setting state.list to:', {
        list,
        listLength: list.length,
        listType: typeof list,
        isArray: Array.isArray(list)
      });
      
      state.list = list;
      state.meta = payload.meta;
      
      console.log('State after fulfilled:', {
        list: state.list,
        listLength: state.list?.length,
        loading: state.loading,
        error: state.error
      });
    });

    builder.addCase(loadPendingNotifications.rejected, (state, action) => {
      console.error('loadPendingNotifications REJECTED:', {
        action,
        payload: action.payload,
        error: action.error
      });
      
      state.loading = false;
      state.error = action.payload?.message || action.error?.message || "Unable to load notifications";
    });
  },
});

export const { clearNotificationError } = notificationSlice.actions;
export default notificationSlice.reducer;

